import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import styles from './Calendar.module.css'
import {CreateAppModal} from '../../../../_metronic/partials/modals/daily-monitoring-stepper/CreateAppModal'
import DailyMonitoringDetail from './DailyMonitoringDetail'
import {useAuth} from '../../../modules/auth'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {Modal, Button} from 'react-bootstrap'

interface CalendarPageProps {
  claimDetails: any
  currentView?: string
  status?: string
  hasNewNotification?: boolean
  notificationCount?: number
  handleViewNavigation?: (direction: 'prev' | 'next') => void
  setIsChatOpen?: (isOpen: boolean) => void
  handleRequestIGL?: () => void
  handleRequestFGL?: () => void
  documentsUploaded?: boolean
  onStatusUpdate?: (newStatus: string) => void
}

const CalendarPage: React.FC<CalendarPageProps> = ({
  claimDetails,
  currentView = 'calendar',
  status,
  hasNewNotification,
  notificationCount,
  handleViewNavigation,
  setIsChatOpen,
  handleRequestIGL,
  handleRequestFGL,
  documentsUploaded,
  onStatusUpdate,
}) => {
  const user = useAuth()
  const {auth} = useAuth()
  const {formatMessage: f} = useIntl()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<number | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [viewDetail, setViewDetail] = useState(false)
  const [specialDates, setSpecialDates] = useState<string[]>([])
  const [claimNumber, setClaimNumber] = useState(claimDetails.claimDetails.claim.claimNumber)
  const [monitoringDetails, setMonitoringDetails] = useState(null)
  const [formattedDate, setFormattedDate] = useState('')
  const [isStatus, setIsStatus] = useState(claimDetails.claimDetails.claim.status)
  const [monitoringDetailsByYesterday, setMonitoringDetailsByYesterday] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // Add state for custom warning modal
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [warningMessage, setWarningMessage] = useState('')

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const daysInMonth = Array.from({length: getDaysInMonth(currentMonth)}, (_, i) => i + 1)
  const firstDayIndex = getFirstDayOfMonth(currentMonth)
  const totalCells = firstDayIndex + daysInMonth.length
  const remainingCells = 42 - totalCells

  // Navigate between months
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
    setSelectedDate(null)
  }

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
    setSelectedDate(null)
  }

  const formatMonthYear = (date: Date) => {
    return date.toLocaleString('default', {month: 'long', year: 'numeric'})
  }

  const handleDateSelect = (day: number, e: React.MouseEvent) => {
    // Prevent triggering the modal if clicking the cell directly or any child elements not meant for the action
    if (
      !(e.target as HTMLElement).classList.contains(styles.addButton) &&
      !(e.target as HTMLElement).classList.contains(styles.viewButton)
    ) {
      return
    }

    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    const formattedMonth = month < 10 ? `0${month}` : month
    const formattedDay = day < 10 ? `0${day}` : day
    const formattedDate = `${year}-${formattedMonth}-${formattedDay}`
    setFormattedDate(formattedDate)
    setSelectedDate(day)
    setShowModal(true)
  }

  const handleViewDetail = (day: number) => {
    setSelectedDate(day)
    setViewDetail(true)
    const dateStr = `${currentMonth.getFullYear()}-${currentMonth.getMonth() + 1}-${day}`
    const url = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/DailyMonitoring?ClaimNumber=${claimNumber}&Date=${dateStr}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    fetch(url, {headers})
      .then((response) => response.json())
      .then((data) => {
        setMonitoringDetails(data)
      })
      .catch((error) => console.error('Failed to fetch monitoring details', error))
  }

  const handleBackToCalendar = () => {
    setViewDetail(false)
    setSelectedDate(null)
  }

  const refreshCalendarData = () => {
    return fetch(
      `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/GetListDateClaimMonitoring?claimNumber=${claimNumber}`,
      {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        console.log('Updated monitoring dates from API:', data)
        setSpecialDates(data)
        // Force refresh by updating the key
        setRefreshKey((prev) => prev + 1)
        return data
      })
      .catch((error) => {
        console.error('Failed to fetch special dates', error)
        return []
      })
  }

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/GetListDateClaimMonitoring?claimNumber=${claimNumber}`,
      {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        // Store dates exactly as they come from API without reformatting
        console.log('Monitoring dates from API:', data)
        setSpecialDates(data)
      })
      .catch((error) => console.error('Failed to fetch special dates', error))
  }, [])

  const isPastDate = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  const handleModalSuccess = () => {
    refreshCalendarData()
    if (onStatusUpdate) {
      onStatusUpdate('Daily Monitoring')
    }
  }

  // Parse the indate from claimDetails to use for comparison
  const [indateValue, setIndateValue] = useState<Date | null>(null)

  // Initialize indate when component loads
  useEffect(() => {
    // Access indate from the correct location in claimDetails
    if (claimDetails?.claimDetails?.claim?.indate) {
      setIndateValue(new Date(claimDetails.claimDetails.claim.indate))
      console.log('Indate set to:', new Date(claimDetails.claimDetails.claim.indate))
    }
  }, [claimDetails])

  // Modified function to handle Add button click
  // Modified function to handle Add button click
  const handleAddMonitoring = (day: number) => {
    // Format the selected date
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    const formattedMonth = month < 10 ? `0${month}` : month
    const formattedDay = day < 10 ? `0${day}` : day
    const selectedDateStr = `${year}-${formattedMonth}-${formattedDay}`

    // Always refresh data before opening modal
    fetch(
      `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/GetListDateClaimMonitoring?claimNumber=${claimNumber}`,
      {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        // Update specialDates with fresh data
        setSpecialDates(data)

        // Open modal directly — ICD10 will be pre-filled from claimDetails in CreateAppModal
        setFormattedDate(selectedDateStr)
        setSelectedDate(day)
        setMonitoringDetailsByYesterday(null)
        setShowModal(true)
      })
      .catch((error) => {
        console.error('Failed to fetch updated special dates', error)
        alert(`Error refreshing data. Please try again.`)
      })
  }

  // Function to handle modal close
  const handleModalClose = () => {
    // Reset modal state
    setShowModal(false)
    setSelectedDate(null)
    setFormattedDate('')
    setMonitoringDetailsByYesterday(null)

    // Refresh special dates data to ensure we have the latest data
    refreshCalendarData()
  }

  if (viewDetail && selectedDate !== null) {
    return (
      <div className='container-fluid'>
        {/* <button className='btn btn-secondary mb-3' onClick={handleBackToCalendar}>
          <i className='fas fa-arrow-left me-2'></i>Testa
        </button> */}
        <DailyMonitoringDetail details={monitoringDetails} onBack={handleBackToCalendar} />
      </div>
    )
  }

  return (
    <>
      {/* Place ToastContainer at the top level */}
      <ToastContainer
        position='top-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
      />

      {/* Custom Warning Modal */}
      <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{f({id: 'CLAIM.INPATIENT.WARNING'})} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{warningMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowWarningModal(false)}>
            {f({id: 'CLAIM.INPATIENT.CLOSE'})}
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        className='d-flex flex-column'
        key={`calendar-container-${refreshKey}`}
        style={{width: '120%'}}
      >
        <div className={styles['calendar-container']} style={{padding: '20px 80px'}}>
          {/* Info Banner */}
          <div
            style={{
              backgroundColor: '#e9f5f8',
              borderLeft: '4px solid #059BB6',
              padding: '12px 16px',
              marginBottom: '20px',
              borderRadius: '4px',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <i
              className='fas fa-info-circle me-2'
              style={{color: '#059BB6', fontSize: '1.2rem'}}
            ></i>
            <span>{f({id: 'CLAIM.INPATIENT.CALENDAR_ADD_HINT'})}</span>
          </div>

          <div className='card shadow-sm'>
            <div className='card-header d-flex justify-content-between align-items-center'>
              <button className='btn btn-icon btn-light-primary' onClick={handlePrevMonth}>
                <i className='fas fa-chevron-left'></i>
              </button>
              <h3 className='card-title text-center'>{formatMonthYear(currentMonth)}</h3>
              <div className='d-flex align-items-center gap-2'>
                <button className='btn btn-icon btn-light-primary' onClick={handleNextMonth}>
                  <i className='fas fa-chevron-right'></i>
                </button>
              </div>
            </div>
            <div className='card-body'>
              <div className={styles.calendarGrid}>
                {daysOfWeek.map((day) => (
                  <div key={day} className={styles.calendarHeader}>
                    {day}
                  </div>
                ))}

                {Array.from({length: firstDayIndex}).map((_, i) => (
                  <div key={`empty-${i}`} className={styles.calendarCell}></div>
                ))}

                {daysInMonth.map((day) => {
                  const currentDate = new Date(
                    currentMonth.getFullYear(),
                    currentMonth.getMonth(),
                    day
                  )

                  // Format current calendar cell date in the exact format expected from API (YYYY-MM-DD)
                  const currentYear = currentMonth.getFullYear()
                  const currentMonthNumber = currentMonth.getMonth() + 1
                  const formattedMonth =
                    currentMonthNumber < 10 ? `0${currentMonthNumber}` : `${currentMonthNumber}`
                  const formattedDay = day < 10 ? `0${day}` : `${day}`
                  const formattedCurrentDate = `${currentYear}-${formattedMonth}-${formattedDay}`

                  const isToday = currentDate.toDateString() === new Date().toDateString()

                  // Direct string comparison with API data
                  const hasMonitoringData = specialDates.includes(formattedCurrentDate)

                  // Check if date is on or after indate
                  const isAfterOrEqualIndate =
                    indateValue &&
                    new Date(formattedCurrentDate) >= new Date(indateValue.setHours(0, 0, 0, 0))

                  // Check if date is before or equal to today
                  const isBeforeOrEqualToday = new Date(formattedCurrentDate).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)

                  // Show Add button only for dates that:
                  // 1. Are on or after indate
                  // 2. Don't have monitoring data already
                  // 3. Are on or before today
                  // 4. Claim status is 'IGL Released' or 'Daily Monitoring'
                  const shouldShowAddButton =
                    isAfterOrEqualIndate &&
                    !hasMonitoringData &&
                    isBeforeOrEqualToday &&
                    (claimDetails?.claimDetails?.claim?.status === 'IGL Released' ||
                      claimDetails?.claimDetails?.claim?.status === 'Daily Monitoring')

                  return (
                    <div
                      key={day}
                      className={`${styles.calendarCell} 
                      ${isToday ? styles.today : ''} 
                      ${selectedDate === day ? styles.selected : ''}`}
                    >
                      <div className={styles.calendarDay}>{day}</div>

                      {/* Show View button if this date has monitoring data */}
                      {hasMonitoringData && (
                        <div className='d-flex justify-content-center'>
                          <button
                            className={`btn btn-icon btn-light-primary btn-sm ${styles.viewButton}`}
                            style={{width: '80%'}}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewDetail(day)
                            }}
                          >
                            <i className='fas fa-eye me-1'></i> {f({id: 'CLAIM.INPATIENT.VIEW'})}
                          </button>
                        </div>
                      )}

                      {isToday && (
                        <div className={styles.todayLabel}>{f({id: 'CLAIM.INPATIENT.TODAY'})}</div>
                      )}

                      {/* Modified Add button to use the new handler */}
                      {shouldShowAddButton && (
                        <div className='d-flex justify-content-center'>
                          <button
                            className={`btn btn-icon btn-light-primary btn-sm ${styles.addButton}`}
                            style={{width: '80%'}}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAddMonitoring(day)
                            }}
                          >
                            <i className='fas fa-plus me-1'></i> {f({id: 'CLAIM.INPATIENT.ADD'})}
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}

                {Array.from({length: remainingCells}).map((_, i) => (
                  <div key={`empty-${i}`} className={styles.calendarCell}></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <CreateAppModal
          show={showModal}
          handleClose={handleModalClose}
          claimDetails={claimDetails}
          formattedDate={formattedDate}
          onSuccessSubmit={handleModalSuccess}
          monitoringDetailsByYesterday={monitoringDetailsByYesterday}
        />
      )}
    </>
  )
}

export default CalendarPage
